import React, {FC, ReactNode} from "react"

import {FormattedMessage} from "@irati/utils/lang"
import isContentKey from "@irati/utils/lang/helpers/isContentKey"
import Flex from "@irati/ui/src/Flex"
import {Body} from "@irati/ui/src/Typography"
import {useTheme} from "@irati/ui/src/global/useTheme"
import Close from "@irati/ui/src/Close"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import Button from "@irati/ui/src/Button"
import {OverlayPlacement, Tooltip} from "@irati/ui/src/Tooltip"
import {SpacingProps} from "../Spacing"
import {ButtonWrapper, IconWrapper, StyledCard, StyledIcon, StyledMarkdown} from "./styled"

export const AlertTypes = {
  warning: "warning",
  error: "error",
  info: "info",
  success: "success",
  tip: "tip"
} as const

type AlertType = (typeof AlertTypes)[keyof typeof AlertTypes]

interface Props extends SpacingProps {
  title: string | ReactNode
  subTitle?: string | ReactNode
  link?: string
  values?: object
  onClickLink?: () => void
  type: AlertType
  nonDismissable?: boolean
  tooltip?: string
  onHide?: () => void
  noMargin?: boolean
  fontWeight?: "light" | "semibold" | "bold"
}

const Alert: FC<Props> = ({
  title,
  subTitle,
  link,
  values,
  onClickLink,
  type,
  nonDismissable,
  onHide,
  noMargin,
  tooltip,
  fontWeight = "bold",
  ...props
}) => {
  const theme = useTheme()

  const bgColor = () => {
    if (type === AlertTypes.warning) {
      return theme.colors.utility.bgWarning
    }
    if (type === AlertTypes.error) {
      return theme.colors.utility.bgError
    }
    if (type === AlertTypes.info) {
      return theme.colors.primary1_50
    }
    if (type === AlertTypes.success) {
      return theme.colors.utility.bgSuccess
    }
    if (type === AlertTypes.tip) {
      return theme.colors.secondary1_50
    }
    return theme.colors.primary1_50
  }

  const icon = () => {
    if (type === AlertTypes.warning) {
      return IconTypes.circleAlertFill
    }
    if (type === AlertTypes.error) {
      return IconTypes.circleAlertFill
    }
    if (type === AlertTypes.info) {
      return IconTypes.circleInformation
    }
    if (type === AlertTypes.success) {
      return IconTypes.tick
    }
    if (type === AlertTypes.tip) {
      return IconTypes.circleQuestion
    }
    return IconTypes.circleInformation
  }

  const iconColor = () => {
    if (type === AlertTypes.warning) {
      return theme.colors.utility.warning
    }
    if (type === AlertTypes.error) {
      return theme.colors.utility.error
    }
    if (type === AlertTypes.info) {
      return theme.colors.primary1
    }
    if (type === AlertTypes.success) {
      return theme.colors.utility.success
    }
    if (type === AlertTypes.tip) {
      return theme.colors.success
    }
    return theme.colors.primary1
  }

  const titleComponent = isContentKey(title) ? <FormattedMessage id={title} values={values} /> : title

  return (
    <StyledCard bgColor={bgColor()} padding={2} marginBottom={noMargin ? 0 : 2} {...props}>
      <Flex justifyContent="space-between" position="relative" width="100%">
        <Flex justifyContent="space-between" width="100%">
          <Flex direction="column" alignItems="flex-start">
            <Flex marginBottom={subTitle || link ? 1 : 0}>
              <IconWrapper color={iconColor()} justifyContent="center" alignItems="center">
                <StyledIcon
                  icon={icon()}
                  color={iconColor()}
                  bgColor={theme.colors.greys.white}
                  size={type === AlertTypes.success ? 10 : 20}
                />
              </IconWrapper>
              <Body small weight={fontWeight} color={theme.colors.greys.black} marginLeft maxWidth="100%">
                {titleComponent}
              </Body>
            </Flex>
            {subTitle && <StyledMarkdown source={subTitle} componentProps={{body: {small: true}}} />}
            {link && (
              <Button tertiary onClick={onClickLink} marginTop={subTitle ? 1 : 0}>
                <FormattedMessage id={link} />
                <Icon icon={IconTypes.angleRight} size={20} />
              </Button>
            )}
          </Flex>
        </Flex>
        {!nonDismissable && !tooltip && (
          <ButtonWrapper alignItems="flex-start" justifyContent="center">
            <Close onClick={onHide} color={theme.colors.greys.black} />
          </ButtonWrapper>
        )}
        {nonDismissable && tooltip && (
          <IconWrapper color="transparent" justifyContent="center" alignItems="center">
            <Tooltip
              tooltipChildren={
                <Body small padding={[1, 1]}>
                  <FormattedMessage id={tooltip} />
                </Body>
              }
              placement={OverlayPlacement.Top}>
              <StyledIcon icon={IconTypes.information} bgColor={theme.colors.utility.ink} size={20} />
            </Tooltip>
          </IconWrapper>
        )}
      </Flex>
    </StyledCard>
  )
}

export default Alert
